import { useEffect, useMemo, useState } from 'react';
import { presidentPower, type PresidentLegal, type PresidentView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, SeatView, useNarrow, useSpeech } from './common';

const TITLES = ['pr.president', 'pr.vice', 'pr.neutral', 'pr.scum'] as const;

export function PresidentTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as PresidentView;
  const legal = useGame((s) => s.legal) as PresidentLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const [picked, setPicked] = useState<string[]>([]);
  const hand = useMemo(() => [...view.hand].sort((a, b) => presidentPower(a) - presidentPower(b) || a.localeCompare(b)), [view.hand]);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && !!turn?.seats.includes(mySeat) && view.phase === 'PLAYING';
  const narrow = useNarrow();
  const size = narrow ? 'md' : 'lg';
  const ended = view.phase === 'MATCH_END';
  const clearing = view.phase === 'CLEAR';
  const sets = legal?.sets ?? [];

  // A new turn or pile starts from a clean selection.
  useEffect(() => setPicked([]), [view.pileNo, view.turn, view.phase, view.hand.length]);

  /** A card can join the selection if some legal set holds the selection plus that card. */
  const enabled = (c: string) => myTurn && sets.some((s) => s.includes(c) && picked.every((p) => s.includes(p)));
  const exact = sets.some((s) => s.length === picked.length && picked.every((p) => s.includes(p)));

  const toggle = (c: string) => {
    if (!myTurn) return;
    if (picked.includes(c)) setPicked(picked.filter((p) => p !== c));
    else if (enabled(c)) setPicked([...picked, c]);
    else if (sets.some((s) => s.includes(c))) setPicked([c]);
  };
  const play = () => void act({ type: 'play', cards: picked });
  const pass = () => void act({ type: 'pass' });

  const hint = !myTurn ? null : view.need === 0 ? t('pr.leadHint') : t('pr.followHint', { n: view.need });
  const placeOf = (s: number) => view.out.indexOf(s);
  const shown = view.pile.slice(-3);
  const top = view.pile.at(-1);
  const note = clearing
    ? view.clearedBy === 'two'
      ? t('pr.clearedTwo', { name: name(view.lastPlayer) })
      : t('pr.clearedPasses', { name: name(view.turn) })
    : null;
  const ranking = [...view.out, ...[0, 1, 2, 3].filter((s) => !view.out.includes(s)).sort((a, b) => (view.handCounts[a] ?? 0) - (view.handCounts[b] ?? 0))];

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => {
          const place = placeOf(s);
          return (
            <SeatView
              key={s}
              seat={s}
              info={seats[s]}
              mySeat={mySeat}
              total={4}
              turn={turn}
              speech={speech[s]}
              folded={place >= 0}
              sub={<span className="dim num">{t('pr.cards', { n: view.handCounts[s] ?? 0 })}</span>}
              tags={
                <>
                  {place >= 0 && <span className="tag seen">{ended ? t(TITLES[place] ?? 'pr.scum') : t('pr.out', { n: place + 1 })}</span>}
                  {!ended && place < 0 && view.passed[s] && <span className="tag">{t('pr.passed')}</span>}
                </>
              }
            />
          );
        })}
        <div className="felt-center centered">
          <div className="center-note">{t('pr.pile', { n: view.pileNo })}</div>
          <div className={`trick ${clearing ? 'collect' : ''}`} key={`pile-${view.pileNo}-${clearing}`} aria-label="Cards on the table" style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center', minHeight: 90 }}>
            {shown.length === 0 && <span className="dim">{t('pr.empty')}</span>}
            {shown.map((p, i) => (
              <div key={`${p.seat}-${p.cards.join('')}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: p === top ? 1 : 0.55, transform: p === top ? undefined : 'scale(0.85)' }}>
                <div style={{ display: 'flex' }}>
                  {p.cards.map((c, j) => (
                    <div key={c} style={{ marginLeft: j === 0 ? 0 : narrow ? -22 : -30, zIndex: j + i }}>
                      <PlayingCard id={c} size={narrow ? 'sm' : 'md'} />
                    </div>
                  ))}
                </div>
                <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{name(p.seat)}</span>
              </div>
            ))}
          </div>
          {note && (
            <div className="pill accent" role="status">
              {note}
            </div>
          )}
          {ended && (
            <div className="card glass" style={{ textAlign: 'center' }} role="status">
              <strong>{t('pr.isScum', { name: name(view.out[3] ?? 0) })}</strong>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{t('pr.cards', { n: view.handCounts[mySeat] ?? 0 })}</> : null} />
      </div>
      <div className="table-bottom">
        {myTurn && (
          <div className="action-bar">
            <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.85)' }}>{hint}</span>
            <button className="btn primary" disabled={acting || !exact} onClick={play}>
              {t('pr.play')}
            </button>
            {legal?.canPass && (
              <button className="btn" disabled={acting} onClick={pass}>
                {t('pr.pass')}
              </button>
            )}
          </div>
        )}
        <Hand size={size} label="Your hand" maxStep={0.6}>
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size={size}
              dealIndex={view.pileNo === 1 && view.pile.length === 0 ? i : undefined}
              selected={picked.includes(c)}
              playable={enabled(c)}
              unplayable={myTurn && !enabled(c) && !sets.some((s) => s.includes(c))}
              onClick={() => toggle(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('pr.player')}</th>
              <th>{t('pr.cardsLeft')}</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((s, i) => (
              <tr key={s}>
                <td>{i + 1}</td>
                <td className="ellipsis" style={{ maxWidth: 140 }}>
                  {name(s)} {ended ? `· ${t(TITLES[i] ?? 'pr.scum')}` : ''}
                </td>
                <td className="num">{view.out.includes(s) && (view.handCounts[s] ?? 0) === 0 ? t('pr.outShort') : (view.handCounts[s] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('pr.scoreHint')}</p>
      </Modal>
    </>
  );
}
